import { z } from "zod";

/**
 * Konditionen v2 — refactored aus konditionsmatrix.ts.
 *
 * Liegt unter `.bidi-handel/tenants/<tenantId>/konditionen.json`. Ersetzt
 * `konditionsmatrix.json` ab Sprint G2. Bestehende Pilot-Tenants behalten
 * die alte Datei waehrend der Migration parallel — der Bepreisungs-Agent
 * liest erst Konditionen v2, fallback auf konditionsmatrix.
 *
 * Schluessel-Unterschiede zur konditionsmatrix:
 *  - Jeder Eintrag hat eine eindeutige `id` → erlaubt bidirektionale
 *    Edit-Views (Kunde→Artikel + Artikel→Kunde) ohne Sync
 *  - Scope ist EINER VON DREI:
 *      a) artikelCode → Artikel-spezifische Kondition
 *      b) warengruppe → Warengruppen-spezifisch (alle Artikel der WG)
 *      c) beide leer → Generalrabatt fuer den Kunden
 *  - Optionale Gueltigkeit (gueltigAb/gueltigBis ISO-Datum) fuer
 *    zeitlich befristete Sonderpreise
 *  - createdAt/updatedAt fuer Audit
 *
 * Resolution-Priorisierung im Bepreisungs-Agent:
 *   artikelCode > warengruppe > Generalrabatt
 *   Sonderpreis schlaegt Rabatt% (wie bisher)
 *
 * NICHT umbenennen: die Anwendungslogik (Sonderpreis vs Rabatt%) bleibt
 * identisch zur konditionsmatrix v1 — nur das Speicher-Schema wird
 * normalisiert.
 */

export const KonditionScopeSchema = z
  .object({
    artikelCode: z.string().optional(),
    warengruppe: z.string().optional(),
  })
  .refine(
    (v) => !(v.artikelCode && v.warengruppe),
    "Kondition darf NICHT gleichzeitig artikelCode UND warengruppe haben — Scope ist ein-aus-drei"
  );
export type KonditionScope = z.infer<typeof KonditionScopeSchema>;

export const KonditionV2Schema = z
  .object({
    id: z.string().min(1),
    kundeId: z.string().min(1),
    artikelCode: z.string().optional(),
    warengruppe: z.string().optional(),
    rabattProzent: z.number().min(0).max(100).optional(),
    sonderpreis: z.number().nonnegative().optional(),
    bemerkung: z.string().optional(),
    gueltigAb: z.string().optional(),
    gueltigBis: z.string().optional(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
  })
  .refine(
    (v) =>
      v.rabattProzent !== undefined || v.sonderpreis !== undefined,
    "Kondition braucht entweder rabattProzent oder sonderpreis"
  )
  .refine(
    (v) => !(v.artikelCode && v.warengruppe),
    "Kondition darf NICHT gleichzeitig artikelCode UND warengruppe haben"
  );
export type KonditionV2 = z.infer<typeof KonditionV2Schema>;

export const KonditionenV2Schema = z.object({
  schemaVersion: z.literal(1).default(1),
  konditionen: z.array(KonditionV2Schema).default([]),
});
export type KonditionenV2 = z.infer<typeof KonditionenV2Schema>;

/**
 * Scope-Helper — gibt einen kurzen Label-String fuer UI/Logs zurueck:
 *   "Artikel ROH-KS-240"  / "Warengruppe rohbau"  / "Generalrabatt"
 */
export function konditionScopeLabel(k: KonditionV2): string {
  if (k.artikelCode) return `Artikel ${k.artikelCode}`;
  if (k.warengruppe) return `Warengruppe ${k.warengruppe}`;
  return "Generalrabatt";
}

/**
 * Score fuer die Resolution-Priorisierung — hoeher gewinnt:
 *   artikelCode=10 > warengruppe=5 > Generalrabatt=1
 *
 * Bepreisungs-Agent nutzt das, um den spezifischsten passenden Eintrag
 * pro Position zu finden.
 */
export function konditionScopeScore(k: KonditionV2): number {
  if (k.artikelCode) return 10;
  if (k.warengruppe) return 5;
  return 1;
}
